import { KnowledgeArticle, createArticle } from '../types'

export const krisArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'kri-overview',
    title: 'Key Risk Indicators Overview',
    section: 'kris',
    summary: 'KRIs are metrics that signal potential risk changes. Track thresholds, trends, and get alerts when indicators breach acceptable levels.',
    content: `
# Key Risk Indicators (KRIs)

Key Risk Indicators are measurable values that provide early warning signals about risk levels in your organization.

## What Are KRIs?

KRIs help you:
- **Detect** - Early warning of emerging risks
- **Monitor** - Ongoing visibility into risk levels
- **Respond** - Trigger actions when thresholds are breached
- **Report** - Quantifiable metrics for executives

## KRI vs KPI

- **KRI (Key Risk Indicator)** - Measures risk exposure
- **KPI (Key Performance Indicator)** - Measures performance

Example:
- KPI: "99.9% uptime achieved"
- KRI: "3 unpatched critical vulnerabilities"

## KRI Categories

### Security KRIs
- Open critical vulnerabilities
- Days since last security training
- Failed login attempts
- Unpatched systems percentage

### Compliance KRIs
- Controls past due for review
- Evidence expiration rate
- Audit findings open > 90 days
- Policy acknowledgment completion

### Operational KRIs
- System availability percentage
- Incident response time
- Backup success rate
- Change failure rate

### Vendor KRIs
- Vendors with expired assessments
- High-risk vendor percentage
- Vendor incidents in period
    `.trim(),
    tags: ['kri', 'risk-indicators', 'metrics', 'monitoring', 'thresholds', 'alerts'],
    relatedPages: ['/kris'],
    icon: 'Activity',
    order: 1,
  }),

  createArticle({
    id: 'creating-kris',
    title: 'Creating and Managing KRIs',
    section: 'kris',
    summary: 'Create KRIs with thresholds (green/yellow/red), data sources, calculation methods, and owners. Configure alerts when thresholds are breached.',
    content: `
# Creating and Managing KRIs

Learn how to set up and maintain Key Risk Indicators.

## Creating a KRI

1. Go to **KRIs** page
2. Click **New KRI**
3. Configure the KRI:

### Basic Information
- **Name** - Descriptive name
- **Description** - What this KRI measures
- **Category** - Security, Compliance, Operational, etc.
- **Owner** - Person responsible

### Thresholds
Set three threshold levels:
- **Green (Acceptable)** - e.g., < 5 vulnerabilities
- **Yellow (Warning)** - e.g., 5-10 vulnerabilities
- **Red (Critical)** - e.g., > 10 vulnerabilities

### Data Source
Choose how data is collected:
- **Manual** - Enter values manually
- **Calculated** - Derive from platform data
- **Integration** - Pull from external systems

### Frequency
How often the KRI is measured:
- Daily, Weekly, Monthly, Quarterly

## Updating KRI Values

### Manual KRIs
1. Click on the KRI
2. Click **Update Value**
3. Enter the current value
4. Add notes if needed

### Automatic KRIs
Values update automatically based on:
- Vulnerability scan results
- Control status changes
- Evidence expiration dates
- Other platform data

## KRI Alerts

Configure alerts when thresholds are breached:
- Email notifications
- Dashboard warnings
- Task creation
- Slack/Teams notifications (if integrated)

## Best Practices

1. Start with 5-10 KRIs that matter most
2. Ensure data is reliably available
3. Set realistic thresholds
4. Review and adjust thresholds quarterly
5. Link KRIs to risks in the register
    `.trim(),
    tags: ['kri', 'thresholds', 'alerts', 'configuration', 'monitoring'],
    relatedPages: ['/kris'],
    icon: 'Settings',
    order: 2,
  }),
]
